import { configApiRef, useApi } from "@backstage/core-plugin-api";
import { Config } from "@backstage/config";
import { useMemo, useRef } from "react";
import debug from "debug";
import {
  HomeBannerConfig,
  HomeConfig,
  HomeGridConfig,
  HomeLayoutConfig,
  HomeSection,
  HomeSectionItem,
  HomeSettings,
  PluginWidget,
} from "./types";
import {
  DEFAULT_HOME_CONFIG,
  generateDefaultSections,
} from "./homeDefaults";

const logger = debug("appvia:useHomeConfig");

export function useHomeConfig(): HomeConfig {
  const configApi = useApi(configApiRef);
  const previousConfigRef = useRef<HomeConfig>();

  return useMemo(() => {
    try {
      logger("Loading home page configuration");

      const homeConfigData = configApi.getOptionalConfig("app.home");

      if (homeConfigData) {
        logger("Custom home configuration found, parsing...");
        const config = parseHomeConfig(homeConfigData);
        previousConfigRef.current = config;
        logger("Configuration parsed successfully:", config);
        return config;
      }

      // No custom config, use defaults
      logger("No custom home configuration found, using defaults");
      const defaultConfig = generateDefaultSections();
      previousConfigRef.current = defaultConfig;
      return defaultConfig;
    } catch (error) {
      logger("Error loading home configuration:", error);
      // Return cached config or fallback
      return previousConfigRef.current || DEFAULT_HOME_CONFIG;
    }
  }, [configApi]);
}

function parseHomeConfig(config: Config): HomeConfig {
  const result: HomeConfig = { ...DEFAULT_HOME_CONFIG };

  if (config.has("layout")) {
    result.layout = parseLayout(config.getConfig("layout"));
  }

  if (config.has("banner")) {
    result.banner = parseBanner(config.getConfig("banner"));
  }

  if (config.has("sections")) {
    result.sections = parseSections(config.getConfigArray("sections"));
  }

  if (config.has("plugins")) {
    result.plugins = parsePlugins(config.getConfigArray("plugins"));
  }

  if (config.has("settings")) {
    result.settings = parseSettings(config.getConfig("settings"));
  }

  return result;
}

function parseLayout(config: Config): HomeLayoutConfig {
  const layoutType = resolveLayoutType(config.getOptionalString("type"));

  return {
    type: layoutType,
    columnRatios: config.getOptionalStringArray("columnRatios"),
  };
}

function parseBanner(config: Config): HomeBannerConfig {
  return {
    enabled: config.getOptionalBoolean("enabled") ?? true,
    title: config.getOptionalString("title") || "Welcome",
    subtitle: config.getOptionalString("subtitle"),
    icon: config.getOptionalString("icon") || "home",
  };
}

function parseSections(configs: Config[]): HomeSection[] {
  return configs.map(config => ({
    id: config.getString("id"),
    type: config.getString("type") as any,
    title: config.getString("title"),
    column: resolveColumn(config.getOptionalString("column")),
    priority: config.getOptionalNumber("priority") ?? 100,
    enabled: config.getOptionalBoolean("enabled") ?? true,
    items: config.has("items")
      ? parseItems(config.getConfigArray("items"))
      : undefined,
    grid: config.has("grid") ? parseGrid(config.getConfig("grid")) : undefined,
  }));
}

function parseItems(configs: Config[]): HomeSectionItem[] {
  return configs.map((config) => ({
    title: config.getString("title"),
    description: config.getOptionalString("description"),
    icon: config.getOptionalString("icon"),
    url: config.getOptionalString("url"),
    color: config.getOptionalString("color"),
    openInNewTab: config.getOptionalBoolean("openInNewTab") ?? false,
  }));
}

function parseGrid(config: Config): HomeGridConfig {
  return {
    columns: config.getOptionalNumber("columns") ?? 6,
    gap: config.getOptionalNumber("gap") ?? 4,
  };
}

function parsePlugins(configs: Config[]): PluginWidget[] {
  return configs.map(config => ({
    id: config.getString("id"),
    title: config.getOptionalString("title"),
    column: resolveColumn(config.getOptionalString("column"), "right"),
    priority: config.getOptionalNumber("priority") ?? 100,
    enabled: config.getOptionalBoolean("enabled") ?? true,
    config: config.has("config") ? config.get("config") : undefined,
  }));
}

function parseSettings(config: Config): HomeSettings {
  return {
    showAll: config.getOptionalBoolean("showAll") ?? true,
    maxWidth: config.getOptionalString("maxWidth") || "7xl",
    spacing: config.getOptionalNumber("spacing") ?? 6,
  };
}

function resolveLayoutType(
  value?: string,
): HomeLayoutConfig["type"] {
  return value === "single-column" ? "single-column" : "two-column";
}

function resolveColumn(
  value?: string,
  defaultColumn: "left" | "right" = "left",
): HomeSection["column"] {
  if (value === "left" || value === "right") {
    return value;
  }

  return defaultColumn;
}
